import { atom } from 'jotai';

import { createStableSet } from '@/lib/atomUtils';
import type { CardList, ListCard } from '@/types/lists';

/**
 * Atom storing the user's lists.
 */
export const listsStateAtom = atom<{
  data: CardList[];
  loading: boolean;
}>({
  data: [],
  loading: true,
});

/**
 * Atom storing ALL list cards for the user (across all lists).
 * Keyed by list_id for efficient filtering.
 */
export const listCardsStateAtom = atom<{
  data: ListCard[];
  loading: boolean;
}>({
  data: [],
  loading: true,
});

/**
 * Derived atom to get cards grouped by list ID.
 */
export const listCardsByListAtom = atom((get) => {
  const { data } = get(listCardsStateAtom);
  const map = new Map<string, ListCard[]>();

  for (const card of data) {
    const existing = map.get(card.list_id) ?? [];
    existing.push(card);
    map.set(card.list_id, existing);
  }

  return map;
});

/**
 * Derived atom to get all oracle IDs that are on any list.
 * Useful for checking if a card is on any list.
 * Uses stable reference — returns the same Set object when contents are unchanged,
 * preventing cascading re-renders in CollectionPage/CollectionTable.
 */
const stabilizeListedIds = createStableSet<string>();
export const listedOracleIdsAtom = atom((get) => {
  const { data } = get(listCardsStateAtom);
  return stabilizeListedIds(new Set(data.map((card) => card.oracle_id)));
});

/**
 * Derived atom to get oracle IDs grouped by list ID.
 * Returns Map<listId, Set<oracleId>> for per-list membership filtering.
 * Mirrors cardOracleIdsByDeckAtom from allDeckCards.ts.
 */
export const oracleIdsByListAtom = atom((get) => {
  const cardsByList = get(listCardsByListAtom);
  const result = new Map<string, Set<string>>();

  for (const [listId, cards] of cardsByList) {
    result.set(listId, new Set(cards.map((c) => c.oracle_id)));
  }

  return result;
});

/**
 * Derived atom to get list cards by oracle ID.
 * Returns all list entries for a given card (across all lists).
 */
export const listCardsByOracleIdAtom = atom((get) => {
  const { data } = get(listCardsStateAtom);
  const map = new Map<string, ListCard[]>();

  for (const card of data) {
    const existing = map.get(card.oracle_id) ?? [];
    existing.push(card);
    map.set(card.oracle_id, existing);
  }

  return map;
});
